import {
  AppBar,
  Container,
  Drawer,
  IconButton,
  Paper,
  Toolbar,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AccountCircleOutlined, ChevronRight } from "@material-ui/icons";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { App, Events } from "app-interfaces";
import React from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { calcAge, metricConverter, metricUnit, useStateValue } from "../../config/AppContext";
import { DayTypes, EventType, MonthTypes, ScoreType, UserStatus } from "../../types/StaticTypes";
import { formatDate } from "../../utils/time-utils";
import { HeaderBottomComponent as HbC } from "../Header";
import { RemoveClientModal as Delete } from "../Modals";
import EditToggle from "../Utilities/EditToggle";
import Icons from "../Utilities/Icons";
import LoadingSpinner from "../Utilities/LoadingSpinner";
import { IdRoute } from "./ClientEdit";
import ClientSessions from "./ClientSessions";

const useStyles = makeStyles({
  paper: {
    padding: 10,
    width: "33.33%",
    textAlign: "center",
    boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)",
    "&.stats": {
      padding: 30,
    },
  },
  button: {
    position: "relative",
    display: "inline-block",
    // margin: 'auto',
    marginTop: 10,
    marginBottom: "5px",
    // width: '25%',
    "&.invite, &.reinvite": {
      display: "flex",
      margin: "auto",
      padding: "6px 30px",
    },
    "&.reinvite": {
      // width: '28%',
    },
    width: "fit-content",
  },

  root: {
    background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
    border: 0,
    borderRadius: 3,
    boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
    color: "white",
    height: 48,
    padding: "0 30px",
  },
  span: {
    marginLeft: "15px",
    padding: "5px 15px",
  },
  divTime: {
    fontSize: "15px",
    color: "var(--text-primary)",
  },
  gender: {
    margin: 0,
    width: "fit-content",
    fontSize: 14,
    color: " #9e9e9e",
    textTransform: "none",
  },
  height: {
    padding: 15,
    height: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)",
  },
  gird: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridColumnGap: "15px",
    gridRowGap: "15px",
    textAlign: "center",
  },
  age: {
    margin: 0,
    width: "fit-content",
    fontSize: 14,
    color: " #9e9e9e",
    textTransform: "none",
  },
  status: { fontSize: 20, fontWeight: "bold", color: "#007bff" },
  date: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  clientsDetail: {
    paddingBottom: "60px",
    fontSize: 14,
    width: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? "" : 450,
  },
  logOffMobile: {
    padding: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 20 : 0,
  },
  logoff: {
    color: "#007bff",
    margin: "20px 0",
    textTransform: "none",
    fontSize: 14,
  },
  removeAccount: {
    color: "red",
    cursor: "pointer",
    textAlign: "center",
    marginTop: 20,
    fontWeight: "bold",
  },
  email: {
    display: "block",
    color: "#000000",
    wordBreak: "break-all",
    fontSize: 16,
  },
  drawerContainer: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 60,
    width: 492,
  },
  "& .MuiDrawer-paper": {
    width: 500,
  },
});

export function ClientDetailsRoute(props: IdRoute) {
  return (
    <ClientDetails
      id={props.match.params.id}
      onRemoveClient={() => props.history.push("/clients")}
    />
  );
}

export default function ClientDetails(props: { id: string; onRemoveClient?: () => void }) {
  const { t } = useTranslation();

  const classes = useStyles();
  type StatsProps = { upcoming: number; completed: number; cancelled: number };
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [viewSessions, setViewSessions] = React.useState(false);
  const [currentYear, setCurrentYear] = React.useState<string | null>(
    window.innerWidth > 700 ? `${new Date().getFullYear()}` : null
  );

  const { ic_user } = Icons;

  const { Methods, clients, metric } = useStateValue() as Pick<
    App.State,
    "Methods" | "clients" | "metric"
  >;
  const [stats, setStats] = React.useState<StatsProps>();
  const [clientSessions, setClientSessions] =
    React.useState<Events.ResponseServerEvent<Events.SessionFullThatUsedToBePreview>[]>();
  const currentClient = clients?.find((client) => client.id === props.id);

  // Generate session info section
  const generateSessionInfo = () => {
    if (!stats)
      return (
        <div style={{ width: 398, textAlign: "center" }}>
          <LoadingSpinner />
        </div>
      );

    return (
      <div className="d-flex">
        <Paper className={classes.paper}>
          {clientSessions && !!clientSessions.length && (
            <label style={{ color: "var(--heading)" }}>{t("NEXT")}</label>
          )}
          <span>{generateUpcomingSession()}</span>
        </Paper>
        <Paper className={`${classes.paper} stats`}>
          <span className={classes.status}>{stats.upcoming}</span>
          <label style={{ color: "var(--heading)", margin: 0, textTransform: "uppercase" }}>
            {t("Upcoming")}
          </label>
        </Paper>
        <Paper className={`${classes.paper} stats`}>
          <span className={classes.status}>{stats.completed}</span>
          <label style={{ color: "var(--heading)", margin: 0, textTransform: "uppercase" }}>
            {t("Completed")}
          </label>
        </Paper>
      </div>
    );
  };
  // Generate booking info section
  const generateBookingInfo = (): JSX.Element => {
    const isInvited = currentClient?.reg_status === UserStatus.INVITED;
    return (
      <Paper className="card" style={{ boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)" }}>
        <span className={classes.span}>
          {currentClient && currentClient.reg_status
            ? currentClient.reg_status === "REGISTERED"
              ? t("The client is enabled to book sessions online in your calendar.")
              : currentClient.reg_status === "INVITED"
              ? t("Invitation was sent to client and registration is pending")
              : t("The Client need!")
            : ""}
        </span>

        <span
          style={{
            position: "absolute",
            height: "11px",
            width: "11px",
            backgroundColor:
              currentClient && currentClient.reg_status
                ? currentClient.reg_status === UserStatus.REGISTERED
                  ? "#7ed321"
                  : currentClient.reg_status === UserStatus.INVITED
                  ? "#ffb319"
                  : ""
                : "",
            borderRadius: "100%",
            left: "15px",
            top: "15px",
          }}
        />
        {currentClient?.reg_status !== UserStatus.REGISTERED && (
          <Button
            variant="primary"
            className={`${classes.button} ${isInvited ? "reinvite" : "invite"}`}
            onClick={() => currentClient && Methods?.inviteClientBooking(currentClient.id)}
          >
            {isInvited ? `${t("Re-invite")}` : t("Invite")}
          </Button>
        )}
      </Paper>
    );
  };

  // Generate upcoming session info
  const generateUpcomingSession = () => {
    if (!clientSessions) return <></>;
    const upcomingSession = clientSessions[0];
    if (!upcomingSession)
      return (
        <div className="container" style={{ color: "#9b9b9b", padding: 30 }}>
          {t("NO UPCOMING SESSIONS")}
        </div>
      );
    return (
      <div className="container">
        <div>
          <div className={classes.divTime}>
            {`${new Date(upcomingSession.start).getHours() < 10 ? "0" : ""}${new Date(
              upcomingSession.start
            ).getHours()}:${new Date(upcomingSession.start).getMinutes()}${
              new Date(upcomingSession.start).getMinutes() < 10 ? "0" : ""
            }`}{" "}
            -{" "}
            {`${new Date(upcomingSession.end).getHours() < 10 ? "0" : ""}${new Date(
              upcomingSession.end
            ).getHours()}:${new Date(upcomingSession.end).getMinutes()}${
              new Date(upcomingSession.end).getMinutes() < 10 ? "0" : ""
            }`}
          </div>
          <span>
            {t(`${DayTypes[new Date(upcomingSession.start).getDay()].toLowerCase()}`).toUpperCase()}
          </span>
          <span>{` ${new Date(upcomingSession.start).getDate()} `}</span>
          <span className={classes.date}>
            {t(
              `${MonthTypes[new Date(upcomingSession.start).getMonth()].toLowerCase()}`
            ).toUpperCase()}
          </span>
        </div>
      </div>
    );
  };

  // Get client stats
  // Update current Year for Desktop View
  const handleCurrentYear = (params?: { currentYear: string }) => {
    if (!params) return;
    setCurrentYear(params.currentYear);
  };
  const removeClient = () => {
    currentClient && Methods?.forgetClient(currentClient.id);
    return props.onRemoveClient && props.onRemoveClient();
  };

  React.useEffect(() => {
    if (Methods && currentClient && !clientSessions) {
      const controller = new AbortController();
      const signal = controller.signal;
      Methods.getEvents(
        {
          customer_id: currentClient.id,
          from: formatDate(new Date(), 3),
          limit: 1,
          types: [EventType.Session],
          session_state: "NONE",
        },
        signal
      ).then((events) => {
        setClientSessions(events);
      });

      return () => {
        controller.abort();
      };
    }
  }, [currentClient, Methods, clientSessions]);

  React.useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (Methods && !stats && currentClient) {
      if (signal.aborted) return;

      // has side-effects: setting loading modal state on the ApiProvider
      const getStatsCall = Methods.getStats(
        {
          customer_id: currentClient.id,
        },
        signal
      );

      getStatsCall.then((data) => {
        const { stats } = data[0];
        let completed = 0;
        let cancelled = 0;
        let upcoming = 0;

        if (stats && stats.length > 0) {
          stats.forEach((stat: { state: string; count: number }) => {
            if (stat.state === "COMPLETED") completed += stat.count;
            if (stat.state === "CANCELLED") cancelled += stat.count;
            if (stat.state === "NONE") upcoming += stat.count;
          });
        }

        return setStats({ upcoming, completed, cancelled });
      });
    }

    return () => {
      controller.abort();
    };
  }, [stats, currentClient, Methods]);

  if (!currentClient) return <></>;

  let fullName = `${currentClient.first_name} ${currentClient.last_name}`;

  return (
    <div id="client-details" className={classes.clientsDetail}>
      <section className="section-block">
        <HbC />
        <span
          style={{
            display: "block",
            width: "120px",
            margin: "auto",
            borderRadius: "50%",
            textAlign: "center",
            backgroundColor: "#ffffff",
            position: "absolute",
            right: 0,
            left: 0,
            top: !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 130 : 20,
          }}
        >
          {currentClient.photo ? (
            <img
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "100%",
                border: "3px solid white",
                objectFit: "cover",
              }}
              alt="profile"
              src={currentClient.photo ? currentClient.photo.medium : ic_user}
              onError={(event) => {
                event.currentTarget.src = ic_user;
                event.currentTarget.onerror = null;
                return true;
              }}
            />
          ) : (
            <AccountCircleOutlined
              style={{
                stroke: "#ffffff",
                width: "120px",
                height: "120px",
                borderRadius: "100%",
                border: "3px solid white",
              }}
            />
          )}
        </span>
        <h5
          style={{
            textAlign: "center",
            padding: "40px 0px 15px 0px",
            textTransform: "capitalize",
            fontWeight: "bold",
          }}
        >{`${currentClient.first_name} ${currentClient.last_name}`}</h5>
      </section>
      <section
        // className='section-grid'
        className={classes.gird}
      >
        <Paper
          style={{
            padding: 15,
            height: "fit-content",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)",
          }}
        >
          <h5
            // className='section-heading'
            className={classes.gender}
          >
            {t("Gender")}
          </h5>
          <span style={{ fontSize: 18, color: "#000000" }}>
            {t(`${currentClient.gender.charAt(0)}${currentClient.gender.slice(1).toLowerCase()}`)}
          </span>
        </Paper>
        <Paper
          style={{
            padding: 15,
            height: "fit-content",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)",
          }}
        >
          <h5
            // className='section-heading'
            className={classes.age}
          >
            {t("Age")}
          </h5>
          <span style={{ fontSize: 18, color: "#000000" }}>
            {currentClient.birthday ? `${calcAge(currentClient.birthday)} ${t("Yrs")}` : ""}
          </span>
        </Paper>
        <Paper className={classes.height}>
          <h5
            // className='section-heading'
            style={{
              margin: 0,
              width: "fit-content",
              fontSize: 14,
              color: " #9e9e9e",
              textTransform: "none",
            }}
          >
            {t("Height")}
            {/* {metricUnit('height', metric)} */}
          </h5>
          <span style={{ fontSize: 18, color: "#000000" }}>
            {currentClient.height &&
              metricConverter(currentClient.height, "height", metric).toFixed(2)}{" "}
            {metricUnit("height", metric)}
          </span>
        </Paper>
        <Paper
          style={{
            padding: 15,
            height: "fit-content",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)",
          }}
        >
          <h5
            // className='section-heading'
            style={{
              margin: 0,
              width: "fit-content",
              fontSize: 14,
              color: " #9e9e9e",
              textTransform: "none",
            }}
          >
            {t("Weight")}
            {/* {metricUnit(`${ScoreType[4]}`, metric)} */}
          </h5>
          <span style={{ fontSize: 18, color: "#000000" }}>
            {currentClient.weight &&
              metricConverter(currentClient.weight, `${ScoreType[4]}`, metric).toFixed(2)}{" "}
            {metricUnit(`${ScoreType[4]}`, metric)}
          </span>
        </Paper>
      </section>
      <h5
        className="section-heading"
        style={{
          color: "#007bff",
          textTransform: "none",
          fontSize: 14,
        }}
      >
        {t("Contact information")}
      </h5>
      <section>
        <Paper
          style={{
            padding: "0 15px 15px",
            height: "fit-content",
            boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)",
          }}
        >
          <div className="d-flex justify-content-between">
            <label style={{ color: "var(--heading)", fontSize: 14 }}>
              {t("Phone")}
              <span style={{ display: "block", color: "#000000", fontSize: 16 }}>
                {currentClient.phone}
              </span>
            </label>
            <label style={{ color: "var(--heading)", fontSize: 14 }}>
              {t("Email")}
              <span
                // style={}
                className={classes.email}
              >
                {currentClient.email}
              </span>
            </label>
          </div>
          <div /*style={{ gridColumn: 'span 2' }}*/>
            <label style={{ color: "var(--heading)", fontSize: 14 }}>{t("Address")}</label>
            <span style={{ display: "block", color: "#000000", fontSize: 16 }}>
              {currentClient.street && `${currentClient.street}, `}
              {currentClient.city && `${currentClient.city}, `}
              {currentClient.country && `${currentClient.country.name}, `}
              {currentClient.postal_code && `${currentClient.postal_code}`}
            </span>
          </div>
        </Paper>
      </section>
      <h5 className={classes.logoff}>{t("Notes")}</h5>
      <Paper style={{ padding: 15, boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)" }}>
        <p style={{ color: "var(--heading)" }}>
          {currentClient.notes && currentClient.notes.length > 0
            ? currentClient.notes
            : t("No note added")}
        </p>
      </Paper>
      {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? (
        <NavLink to={`/clients/sessions/${currentClient.id}`}>
          <Paper>
            <h5 className={`${classes.logoff} ${classes.logOffMobile}`}>
              {t("Log of all previous and upcoming sessions")}
              <ChevronRight style={{ float: "right" }} />
            </h5>
          </Paper>
        </NavLink>
      ) : (
        <Paper
          style={{
            padding: "0.1px 15px",
            margin: "15px 0px",
            boxShadow: "0 2px 8px 0 rgba(195,195,195,0.45)",
            cursor: "pointer",
          }}
          onClick={() => setViewSessions(true)}
        >
          <h5 className={classes.logoff}>
            {t("Log of all previous and upcoming sessions")}
            <ChevronRight style={{ float: "right", marginTop: -3 }} />
          </h5>
        </Paper>
      )}
      <section className="section-block">{generateSessionInfo()}</section>
      <h5 className={classes.logoff}>{t("Online booking")}</h5>
      <section className="section-block">{generateBookingInfo()}</section>
      <section
        className="section-block"
        style={{ marginTop: "35px", border: "1px solid red" }}
        onClick={() => setShowConfirmation(true)}
      >
        <p className={classes.removeAccount}>{t("Remove account")}</p>
      </section>

      <Drawer
        classes={{
          paper: "500",
        }}
        anchor="right"
        open={viewSessions}
        onClose={(_) => setViewSessions(false)}
      >
        <AppBar position="fixed" style={{ width: 500, backgroundColor: "rgb(62, 127, 233)" }}>
          <Toolbar>
            <div className="d-flex flex-column" style={{ width: "100%" }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <IconButton onClick={(_) => setViewSessions(false)}>
                    <ArrowBackIcon style={{ color: "#ffffff" }} />
                  </IconButton>

                  <Typography variant="h6" noWrap>
                    {t("Session with")}{" "}
                    <span
                      className="text-capitalize"
                      title={`${currentClient?.first_name
                        .charAt(0)
                        .toUpperCase()}${currentClient?.first_name.slice(
                        1
                      )} ${currentClient?.last_name
                        .charAt(0)
                        .toUpperCase()}${currentClient?.last_name.slice(1)}`}
                    >
                      {fullName.length > 21
                        ? `${fullName.slice(0, 20)}...`
                        : `${currentClient.first_name} ${currentClient.last_name}`}
                    </span>
                  </Typography>
                </div>
                <EditToggle
                  id={props.id}
                  show={true}
                  action={handleCurrentYear}
                  currentPath="client-sessions"
                />
              </div>
            </div>
          </Toolbar>
        </AppBar>
        <Container className={classes.drawerContainer}>
          <ClientSessions id={props.id} currentYear={Number(currentYear)} />
        </Container>
      </Drawer>
      <Delete
        show={showConfirmation}
        onSubmit={removeClient}
        onHide={() => setShowConfirmation(false)}
        message={t("If you remove!")}
        title={t("Remove")}
      />
    </div>
  );
}
